import { Preferences } from '@capacitor/preferences';
import { Account, User } from 'model/IChat';
import { fetchAccountInformation } from 'modules/authentication/helpers/nextauth';
import { appServices } from 'singletons';
import AccountRoles from 'utils/AccountRoles';
import { isCordova } from 'utils/platform';
import { create } from 'zustand';
import {
  StateStorage,
  createJSONStorage,
  persist,
  subscribeWithSelector,
} from 'zustand/middleware';

const storage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    if (isCordova()) {
      try {
        const ret = await Preferences.get({ key: name });
        return ret.value ? JSON.parse(ret.value) : null;
      } catch (error) {
        console.error('failed to get from Preferences storage', name, error);
        return null;
      }
    }
    return null;
  },
  setItem: async (name: string, value: string): Promise<void> => {
    if (isCordova()) {
      try {
        await Preferences.set({
          key: name,
          value: JSON.stringify(value),
        });
      } catch (error) {
        console.error(
          'failed to set to native storage [inner]',
          name,
          value,
          error,
        );
      }
    }
  },
  removeItem: async (name: string): Promise<void> => {
    if (isCordova()) {
      try {
        await Preferences.remove({ key: name });
      } catch (error) {
        console.error('failed to remove from native storage', name, error);
      }
    }
  },
};

export type AuthStateFields = {
  token?: string;
  user?: User;
  spy: any;
  loggedInAs: any;
  loading: boolean;
  account: Account | null;
  realAccount: Account | null;
};

export type AuthStateFunctions = {
  _hasHydrated: boolean;
  setHasHydrated: (hasHydrated: boolean) => void;
  getRoles: () => AccountRoles;
  setUser: (user: any) => void;
  setSpy: (spy: any) => void;
  setAccount: (account: Account | null) => void;
  setRealAccount: (account: Account | null) => void;
  setToken: (token: string) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
  isLoggedIn: () => boolean;
  isAdmin: () => boolean;
  isHost: () => boolean;
  isOnboardingHost: () => boolean;
  isOnboardingHostWebinar: () => boolean;
};

const initialState: AuthStateFields = {
  token: undefined,
  user: undefined,
  spy: null,
  account: null,
  realAccount: null,
  loggedInAs: null,
  loading: false,
};

export type AuthState = AuthStateFunctions & AuthStateFields;

const useAuthStore = create<AuthState>()(
  subscribeWithSelector(
    persist(
      (set, get): AuthState => ({
        ...initialState,
        _hasHydrated: false,
        setHasHydrated: (hasHydrated: boolean) =>
          set({ _hasHydrated: hasHydrated }),
        isAdmin: () =>
          Boolean(
            get().isLoggedIn() &&
              (get().user?.roles?.includes('admin') ||
                get().spy?.roles?.includes('admin')),
          ),
        isHost: () =>
          Boolean(get().isLoggedIn() && get().user?.roles?.includes('host')),
        isOnboardingHost: () =>
          Boolean(
            get().isLoggedIn() &&
              get().user?.roles?.includes('onboarding_host'),
          ),
        isOnboardingHostWebinar: () =>
          Boolean(
            get().isLoggedIn() &&
              get().user?.roles?.includes('onboarding_host_webinar'),
          ),
        getRoles: () => new AccountRoles(get().account),
        logout: () => set({ ...initialState }),
        setLoading: (loading: boolean) => set({ loading }),
        setAccount: (account: Account | null) => set({ account }),
        setRealAccount: (account: Account | null) =>
          set({ realAccount: account }),
        setUser: (user: any) => set({ user }),
        setSpy: (spy: any) => set({ spy }),
        setToken: (token: string) => set({ token }),
        isLoggedIn: () => !!get().user,
      }),
      {
        name: 'auth-storage',
        storage: createJSONStorage(() => storage),
        partialize: (state) => ({ token: state.token }),
        skipHydration: isCordova(), // skip auto-hydration, we will handle it manually
        onRehydrateStorage: () => {
          return (state, error) => {
            state?.setHasHydrated(true);
            if (error) {
              console.log('hydrating failed', error, state);
            } else {
              console.log('hydrating', state);
            }
            void fetchAccountInformation(state);
          };
        },
      },
    ),
  ),
);

useAuthStore.subscribe(
  (state) => state.user,
  (user) => {
    if (!user?.id) {
      return;
    }
    console.log(
      'PushMessageHandler: User authenticated. Initializing push service.',
    );
    appServices.permissionFunctions.startPush();
  },
);

export const useUser = () =>
  useAuthStore((state) => ({
    loading: state.loading,
    isLoggedIn: state.isLoggedIn(),
    isHost: state.isHost(),
    isOnboardingHost: state.isOnboardingHost(),
    isOnboardingHostWebinar: state.isOnboardingHostWebinar(),
    isAdmin: state.isAdmin(),
    user: state.user,
  }));

export const useAccount = () =>
  useAuthStore((state) => ({
    loading: state.loading,
    account: state.account,
    realAccount: state.realAccount,
  }));

// @ts-ignore
window.authStore = useAuthStore;

export default useAuthStore;
